import { useBoolean, useCountdown, useEffectOnce } from "usehooks-ts";
import styled from "styled-components";
import * as Sentry from "@sentry/react";

const Text = styled.p`
  text-align: center;
  margin: 0;
`;
interface RedirectComponentProps {
  seconds?: number | undefined;
  redirectToUri: string;
  readableTargetText?: string;
  clientName?: string | undefined;
  t: RedirectText;
}
export interface RedirectText {
  redirectingText: string;
  loadingText: string;
  continueButtonText: string;
}

export default function RedirectComponent({
  seconds,
  redirectToUri,
  readableTargetText,
  clientName,
  t,
}: RedirectComponentProps) {
  const definitionOfSlow = 3000;
  setTimeout(
    () => {
      Sentry.captureMessage(`Slow redirect (${definitionOfSlow}ms)`, "warning");
      Sentry.flush();
    },
    (seconds !== undefined ? seconds : 0) * 1000 + definitionOfSlow,
  );

  const doRedirect = () => {
    Sentry.captureMessage("Replacing location (redirecting user)");
    Sentry.flush(500).finally(() => window.location.replace(redirectToUri));
  };

  if (seconds !== undefined) {
    const [count, { startCountdown }] = useCountdown({
      countStart: seconds,
      intervalMs: 1000,
    });
    useEffectOnce(startCountdown);

    const { setTrue: setRedirected, value: isRedirected } = useBoolean(false);

    if (count > 0) {
      // Create sub elements to support google-translate-like dom manipulation: https://github.com/facebook/react/issues/11538
      return (
        <Text data-testid="redirecting">
          <span>{t.redirectingText} </span>
          {!!clientName && clientName}
          <span> in </span>
          <span>{count}</span>
          <span>s</span>
        </Text>
      );
    } else {
      if (!isRedirected) {
        setRedirected();
        doRedirect();
      }
      return (
        <Text>
          {readableTargetText} {t.loadingText}
        </Text>
      );
    }
  } else {
    useEffectOnce(() => {
      doRedirect();
    });
    return (
      <Text data-testid="redirecting">
        {t.redirectingText} {!!clientName && clientName}
      </Text>
    );
  }
}
